import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { useSocket } from "../../hooks/useSocket";
import { useSocketContext } from "../../context/SocketContext";
import {
  addNotification,
  loadNotificationsFromHistory,
  markAsRead,
  markAllAsRead,
  toggleNotificationPanel,
  setNotificationPanelOpen,
} from "../../redux/slices/notificationSlice";
import "./NotificationBell.css";
import { playOrderPlacedVoice } from "../../utils/voiceNotification";
import notificationService from "../../services/notificationService";
import { toast } from "react-toastify";

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications, unreadCount, isOpen } = useSelector(
    (state) => state.notifications
  );
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token) || localStorage.getItem("authToken");
  const panelRef = useRef(null);
  const { isConnected } = useSocketContext();
  const hasLoadedHistory = useRef(false);
  const isLoadingHistoryRef = useRef(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Debug: Log component mount and state
  useEffect(() => {
    console.log("🔔 NotificationBell Component Mounted/Updated:", {
      hasUser: !!user,
      userId: user?.id,
      hasToken: !!token,
      notificationsCount: notifications.length,
      unreadCount: unreadCount,
      isOpen: isOpen,
      socketConnected: isConnected
    });
  }, [user, token, notifications.length, unreadCount, isOpen, isConnected]);

  // Debug: Log socket connection status
  useEffect(() => {
    console.log("🔌 NotificationBell - Socket connected:", isConnected);
  }, [isConnected]);

  // Load old notifications from history when component mounts or user logs in
  // This runs on every mount (including page refresh) to ensure history is loaded
  useEffect(() => {
    // Wait for token and user to be available
    const currentToken = token || localStorage.getItem("authToken");
    const currentUser = user;
    
    if (!currentToken) {
      console.log("⏭️ Skipping history load - no token");
      return;
    }

    if (!currentUser || !currentUser.id) {
      console.log("⏭️ User not loaded yet, skipping history load");
      return;
    }

    // Prevent multiple simultaneous loads or if already loaded for this user
    if (isLoadingHistoryRef.current) {
      console.log("⏭️ History already loading, skipping...");
      return;
    }

    // Check if we've already loaded for this user
    const loadKey = `${currentUser.id}_${currentToken.substring(0, 10)}`;
    if (hasLoadedHistory.current === loadKey) {
      console.log("⏭️ History already loaded for this user, skipping...");
      return;
    }

    const loadHistory = async () => {
      isLoadingHistoryRef.current = true;
      setIsLoadingHistory(true);
      
      try {
        console.log("📚 Loading notification history from database...", { 
          userId: currentUser.id,
          userType: currentUser.user_type 
        });
        
        const response = await notificationService.getHistory(100);
        console.log("📚 Raw API Response:", response);
        console.log("📚 Response type:", typeof response);
        console.log("📚 Response keys:", Object.keys(response || {}));
        
        // The service returns response.data, so we need to check the structure
        // Backend returns: { success: true, data: { notifications: [...], pagination: {...} } }
        // Service returns: response.data which is the above object
        let notifications = [];
        
        if (response && response.success) {
          // Standard response structure
          notifications = response.data?.notifications || [];
          console.log("📚 Using standard response structure");
        } else if (response && response.data && Array.isArray(response.data.notifications)) {
          // Nested data structure
          notifications = response.data.notifications;
          console.log("📚 Using nested data structure");
        } else if (Array.isArray(response)) {
          // Direct array response
          notifications = response;
          console.log("📚 Using direct array response");
        } else if (response && response.notifications && Array.isArray(response.notifications)) {
          // Alternative structure
          notifications = response.notifications;
          console.log("📚 Using alternative structure");
        } else {
          console.warn("⚠️ Unexpected response structure:", response);
        }
        
        console.log(`📚 Extracted ${notifications.length} notifications from response`);
        console.log("📚 Notifications array:", notifications);
        
        if (notifications.length > 0) {
          console.log(`📚 Found ${notifications.length} notifications in history`);
          console.log("📚 First notification sample:", JSON.stringify(notifications[0], null, 2));
          
          // Verify notification structure
          const firstNotif = notifications[0];
          if (!firstNotif.id) {
            console.warn("⚠️ Notification missing ID:", firstNotif);
          }
          if (!firstNotif.title && !firstNotif.body) {
            console.warn("⚠️ Notification missing title/body:", firstNotif);
          }
          
          // Clear existing notifications first (on page refresh, state is already empty, but ensure clean state)
          console.log("📤 Dispatching loadNotificationsFromHistory with", notifications.length, "notifications");
          dispatch(loadNotificationsFromHistory({
            notifications: notifications,
            markAsRead: false,
            clearExisting: true, // Clear existing on initial load
          }));
          hasLoadedHistory.current = loadKey;
          console.log("✅ Notification history loaded and dispatched to Redux");
        } else {
          console.log("📚 No notifications found in database for user:", currentUser.id);
          console.log("📚 Response was:", response);
          hasLoadedHistory.current = loadKey;
        }
      } catch (error) {
        console.error("❌ Error loading notification history:", error);
        console.error("❌ Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url,
          stack: error.stack,
        });
        hasLoadedHistory.current = loadKey; // Mark as attempted even on error
      } finally {
        isLoadingHistoryRef.current = false;
        setIsLoadingHistory(false);
      }
    };

    // Load immediately when component mounts
    loadHistory();
    
    // Cleanup function
    return () => {
      // Don't reset isLoadingHistoryRef here as it's needed to prevent re-runs
    };
  }, [token, user?.id, dispatch]); // Only re-run when token or user.id changes (removed isLoadingHistory)

  // Refresh notifications when panel opens (only once per open, not on every render)
  const panelOpenRef = useRef(false);
  useEffect(() => {
    // Only refresh if panel just opened (was closed, now open)
    if (isOpen && !panelOpenRef.current) {
      panelOpenRef.current = true;
      
      const refreshHistoryOnOpen = async () => {
        const currentToken = token || localStorage.getItem("authToken");
        const currentUser = user;
        
        if (!currentToken || !currentUser) {
          return;
        }

        // Prevent multiple simultaneous refreshes
        if (isLoadingHistoryRef.current) {
          console.log("⏭️ Already loading, skipping panel refresh");
          return;
        }

        try {
          console.log("🔄 Refreshing notification history on panel open...", { userId: currentUser.id });
          isLoadingHistoryRef.current = true;
          
          const response = await notificationService.getHistory(100);
          console.log("🔄 API Response on open:", response);
          
          // Handle different response structures
          const notifications = response?.data?.notifications || response?.notifications || [];
          
          if (notifications.length > 0) {
            console.log(`🔄 Found ${notifications.length} notifications in history`);
            // Don't clear existing, just merge (to keep real-time notifications)
            dispatch(loadNotificationsFromHistory({
              notifications: notifications,
              markAsRead: false,
              clearExisting: false, // Don't clear, just merge
            }));
          } else {
            console.log("🔄 No notifications found in history");
          }
        } catch (error) {
          console.error("❌ Error refreshing notification history on open:", error);
          console.error("❌ Error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
          });
        } finally {
          isLoadingHistoryRef.current = false;
        }
      };

      // Small delay to avoid double loading with initial mount
      const timer = setTimeout(refreshHistoryOnOpen, 300);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      // Reset when panel closes
      panelOpenRef.current = false;
    }
  }, [isOpen, token, user?.id, dispatch]);

  // Debug: Log notification state
  useEffect(() => {
    console.log("🔔 NotificationBell - Unread count:", unreadCount);
    console.log("🔔 NotificationBell - Total notifications:", notifications.length);
    if (notifications.length > 0) {
      console.log("🔔 NotificationBell - Latest notification:", notifications[0]);
    }
  }, [unreadCount, notifications]);

  // Track processed notifications to prevent duplicates
  // Format: "eventType_orderId_status" to track unique notifications
  const processedNotifications = useRef(new Set());
  
  // Track orders recently cancelled by current user to skip socket toast
  // (API already shows success toast, so we don't need socket toast)
  const recentlyCancelledOrders = useRef(new Set());

  // Helper function to create unique notification key
  const getNotificationKey = (eventType, orderId, status = null) => {
    if (status) {
      return `${eventType}_${orderId}_${status}`;
    }
    return `${eventType}_${orderId}`;
  };

  // Helper to check if an order event is strictly relevant to the logged-in user
  const isOrderRelevantToUser = (order, currentUser) => {
    if (!order || !currentUser) return false;
    const userType = currentUser.user_type;
    const currentUserId = String(currentUser.id);

    // 1. Super Admin and Admin can see all order events
    if (userType === "admin" || userType === "super_admin") {
      return true;
    }

    // 2. Customer: only their own orders
    if (userType === "customer") {
      return String(order.user_id) === currentUserId;
    }

    // 3. Delivery partner: only orders assigned to them
    if (userType === "delivery") {
      return String(order.delivery_boy_id) === currentUserId;
    }

    // 4. Vendor: only orders containing their products
    if (userType === "vendor") {
      if (String(order.vendor_id) === currentUserId) {
        return true;
      }
      if (order.items && Array.isArray(order.items)) {
        return order.items.some((item) => String(item.vendor_id) === currentUserId);
      }
      return false;
    }

    return false;
  };
  
  // Expose function to mark order as cancelled by current user (called from Orders component)
  useEffect(() => {
    window.markOrderCancelledByUser = (orderId) => {
      recentlyCancelledOrders.current.add(orderId);
      // Remove from set after 5 seconds to allow socket notifications for other cancellations
      setTimeout(() => {
        recentlyCancelledOrders.current.delete(orderId);
      }, 5000);
    };
    
    return () => {
      delete window.markOrderCancelledByUser;
    };
  }, []);

  // Listen for socket notifications
  useSocket("notification", (data) => {
    // If targeted to a specific user, verify it matches
    if (data?.targetUserId && user?.id && String(data.targetUserId) !== String(user.id)) {
      return;
    }

    // Skip if this is a duplicate order notification (we handle order_created separately)
    if (data.data?.type === "order" && data.data?.order_id) {
      const orderId = data.data.order_id;
      const notificationKey = getNotificationKey("order_created", orderId);
      if (processedNotifications.current.has(notificationKey)) {
        console.log("⚠️ Duplicate notification event ignored (already processed as order_created):", orderId);
        return;
      }
      // Mark as processed to prevent duplicate if order_created event comes later
      processedNotifications.current.add(notificationKey);
    }
    
    const notification = {
      type: data.data?.type || "general",
      title: data.title || "Notification",
      message: data.message || data.body || "",
      data: data.data || {},
      ...data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.info(notification.message || notification.title, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  });

  // Listen for order events
  useSocket("order_created", (data) => {
    console.log("📦 Order created event received in NotificationBell:", data);
    if (!data || !data.order) {
      console.warn("⚠️ Invalid order_created event data:", data);
      return;
    }

    const order = data.order;
    const orderId = order?.id;
    if (!orderId) {
      console.warn("⚠️ Order ID missing in event data");
      return;
    }

    // Validate relevance to current user
    if (!isOrderRelevantToUser(order, user)) {
      console.log("⏭️ Order created event not relevant to current user, ignoring:", orderId);
      return;
    }

    const notificationKey = getNotificationKey("order_created", orderId);

    // Prevent duplicate notifications for the same order
    if (processedNotifications.current.has(notificationKey)) {
      console.log("⚠️ Duplicate order_created notification ignored:", orderId);
      return;
    }

    // Mark this notification as processed
    processedNotifications.current.add(notificationKey);
    
    // Create meaningful notification message based on user role
    const userType = user?.user_type;
    let notificationTitle = "New Order";
    let notificationMessage = data.message;
    
    if (userType === "customer") {
      notificationTitle = "Order Placed";
      notificationMessage = data.message || `Your order #${orderId.slice(-8)} has been placed successfully`;
    } else if (userType === "vendor") {
      notificationTitle = "New Order Received";
      notificationMessage = data.message || `New order #${orderId.slice(-8)} received for your products`;
    } else {
      // Admin or super_admin
      notificationTitle = "New Order";
      notificationMessage = data.message || `New order #${orderId.slice(-8)} placed`;
    }
    
    const notification = {
      type: "order",
      title: notificationTitle,
      message: notificationMessage,
      orderId: orderId,
      data: data,
    };
    console.log("✅ Adding notification to Redux:", notification);
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.success(notificationMessage, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    
    // Play voice notification only for new orders (admin/vendor only, not customer)
    if (userType === "admin" || userType === "super_admin" || userType === "vendor") {
      playOrderPlacedVoice();
    }
  }, [dispatch, user]);

  useSocket("order_status_changed", (data) => {
    console.log("📦 Order status changed event received:", data);
    if (!data || !data.order) {
      console.warn("⚠️ Invalid order_status_changed event data:", data);
      return;
    }

    const order = data.order;
    const orderId = order?.id;
    const newStatus = data.newStatus;
    
    if (!orderId || !newStatus) {
      console.warn("⚠️ Order ID or status missing in event data");
      return;
    }

    // Validate relevance to current user
    if (!isOrderRelevantToUser(order, user)) {
      console.log("⏭️ Order status change not relevant to current user, ignoring:", orderId);
      return;
    }

    // Skip cancelled status - let order_cancelled handler process it to avoid duplicate toasts
    if (newStatus === "cancelled") {
      console.log("⚠️ Skipping order_status_changed for cancelled status - will be handled by order_cancelled event");
      return;
    }

    const notificationKey = getNotificationKey("order_status_changed", orderId, newStatus);

    // Prevent duplicate notifications for the same order status change
    if (processedNotifications.current.has(notificationKey)) {
      console.log("⚠️ Duplicate order_status_changed notification ignored:", orderId, newStatus);
      return;
    }

    // Mark this notification as processed
    processedNotifications.current.add(notificationKey);

    const notification = {
      type: "order",
      title: "Order Status Updated",
      message: data.message || `Order #${orderId} status changed to ${newStatus}`,
      orderId: orderId,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.info(notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [dispatch, user]);

  useSocket("order_assigned", (data) => {
    console.log("📦 Order assigned event received:", data);
    if (!data || !data.order) {
      console.warn("⚠️ Invalid order_assigned event data:", data);
      return;
    }

    const order = data.order;
    const orderId = order?.id;
    if (!orderId) {
      console.warn("⚠️ Order ID missing in event data");
      return;
    }

    // Validate relevance to current user
    if (!isOrderRelevantToUser(order, user)) {
      console.log("⏭️ Order assigned event not relevant to current user, ignoring:", orderId);
      return;
    }

    // Use the same key format as order_status_changed to prevent duplicates
    // When order is accepted, both events might fire, so we track by orderId + "accepted"
    const notificationKey = getNotificationKey("order_status_changed", orderId, "accepted");
    const assignedKey = getNotificationKey("order_assigned", orderId);

    // Prevent duplicate notifications - check both keys
    if (processedNotifications.current.has(notificationKey) || processedNotifications.current.has(assignedKey)) {
      console.log("⚠️ Duplicate order_assigned notification ignored (already processed as status change):", orderId);
      return;
    }

    // Mark both keys as processed to prevent duplicates
    processedNotifications.current.add(notificationKey);
    processedNotifications.current.add(assignedKey);

    const notification = {
      type: "order",
      title: "Order Accepted & Assigned",
      message: data.message || `Order #${orderId} has been accepted and assigned`,
      orderId: orderId,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.success(notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [dispatch, user]);

  useSocket("order_cancelled", (data) => {
    console.log("📦 Order cancelled event received:", data);
    if (!data || !data.order) {
      console.warn("⚠️ Invalid order_cancelled event data:", data);
      return;
    }

    const order = data.order;
    const orderId = order?.id;
    if (!orderId) {
      console.warn("⚠️ Order ID missing in event data");
      return;
    }

    // Validate relevance to current user
    if (!isOrderRelevantToUser(order, user)) {
      console.log("⏭️ Order cancelled event not relevant to current user, ignoring:", orderId);
      return;
    }

    // Skip toast if this order was just cancelled by current user (API already showed success toast)
    if (recentlyCancelledOrders.current.has(orderId)) {
      console.log("⚠️ Skipping socket toast for order cancelled by current user:", orderId);
      // Still add to notifications but don't show toast
      const notification = {
        type: "order",
        title: "Order Cancelled",
        message: data.message || `Order #${orderId} has been cancelled`,
        orderId: orderId,
        data: data,
      };
      dispatch(addNotification(notification));
      return;
    }

    // Use the same key format as order_status_changed to prevent duplicates
    // When order is cancelled, both events might fire, so we track by orderId + "cancelled"
    const notificationKey = getNotificationKey("order_status_changed", orderId, "cancelled");
    const cancelledKey = getNotificationKey("order_cancelled", orderId);

    // Prevent duplicate notifications - check both keys
    if (processedNotifications.current.has(notificationKey) || processedNotifications.current.has(cancelledKey)) {
      console.log("⚠️ Duplicate order_cancelled notification ignored (already processed as status change):", orderId);
      return;
    }

    // Mark both keys as processed to prevent duplicates
    processedNotifications.current.add(notificationKey);
    processedNotifications.current.add(cancelledKey);

    const notification = {
      type: "order",
      title: "Order Cancelled",
      message: data.message || `Order #${orderId} has been cancelled`,
      orderId: orderId,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically (only if not cancelled by current user)
    toast.error(notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [dispatch, user]);

  // Listen for inventory events (Admins & Product Vendors only)
  useSocket("inventory_low_stock", (data) => {
    const userType = user?.user_type;
    const userId = String(user?.id);

    // Customers and delivery partners should NEVER receive inventory low stock notifications
    if (userType !== "admin" && userType !== "super_admin" && userType !== "vendor") {
      return;
    }

    // If vendor, ensure product belongs to this vendor
    if (userType === "vendor" && data.vendor_id && String(data.vendor_id) !== userId) {
      return;
    }

    const notification = {
      type: "inventory",
      title: "Low Stock Alert",
      message: data.message || `Low stock: ${data.product?.name || "Product"}`,
      productId: data.product?.id,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.warning(notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  useSocket("inventory_updated", (data) => {
    const userType = user?.user_type;
    const userId = String(user?.id);

    if (userType !== "admin" && userType !== "super_admin" && userType !== "vendor") {
      return;
    }

    if (userType === "vendor" && data.vendor_id && String(data.vendor_id) !== userId) {
      return;
    }

    const notification = {
      type: "inventory",
      title: "Inventory Updated",
      message: data.message || `Inventory updated for ${data.product?.name || "product"}`,
      productId: data.product?.id,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    toast.info(notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  // Listen for payment events
  useSocket("payment_status_changed", (data) => {
    const order = data.order;
    if (order && !isOrderRelevantToUser(order, user)) {
      return;
    }

    const notification = {
      type: "payment",
      title: "Payment Status Updated",
      message: data.message || `Payment status changed`,
      orderId: data.orderId || order?.id,
      data: data,
    };
    
    dispatch(addNotification(notification));
    
    // Show toast notification automatically
    const paymentStatus = (data.status || order?.payment_status)?.toLowerCase();
    const toastType = paymentStatus === "paid" || paymentStatus === "completed" ? "success" : "info";
    
    toast[toastType](notification.message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  // Listen for return request events
  useSocket("return_created", (data) => {
    const order = data.order;
    if (order && !isOrderRelevantToUser(order, user)) {
      return;
    }

    const returnReq = data.returnRequest || {};
    const orderId = order?.id || returnReq.order_id;
    const userType = user?.user_type;
    let title = "Return Request";
    let message = `New return request created for order #${orderId ? orderId.slice(-8) : ""}`;

    if (userType === "customer") {
      title = "Return Request Submitted";
      message = `Your ${returnReq.type || "return"} request for order #${orderId ? orderId.slice(-8) : ""} was submitted`;
    }

    const notification = {
      type: "return",
      title: title,
      message: message,
      orderId: orderId,
      data: data,
    };

    dispatch(addNotification(notification));
    toast.info(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  useSocket("return_status_changed", (data) => {
    const order = data.order;
    if (order && !isOrderRelevantToUser(order, user)) {
      return;
    }

    const returnReq = data.returnRequest || {};
    const orderId = order?.id || returnReq.order_id;
    const status = data.status || returnReq.status;
    const message = `Return request for order #${orderId ? orderId.slice(-8) : ""} is now ${status}`;

    const notification = {
      type: "return",
      title: "Return Status Updated",
      message: message,
      orderId: orderId,
      data: data,
    };

    dispatch(addNotification(notification));
    toast.info(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  // Listen for refund events
  useSocket("refund_processed", (data) => {
    const order = data.order;
    if (order && !isOrderRelevantToUser(order, user)) {
      return;
    }

    const refund = data.refund || {};
    const orderId = order?.id || refund.order_id;
    const amount = refund.refund_amount ? `₹${parseFloat(refund.refund_amount).toFixed(2)}` : "";
    const message = `Refund ${amount ? `of ${amount} ` : ""}processed for order #${orderId ? orderId.slice(-8) : ""}`;

    const notification = {
      type: "refund",
      title: "Refund Processed",
      message: message,
      orderId: orderId,
      data: data,
    };

    dispatch(addNotification(notification));
    toast.success(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  useSocket("refund_status_changed", (data) => {
    const order = data.order;
    if (order && !isOrderRelevantToUser(order, user)) {
      return;
    }

    const refund = data.refund || {};
    const orderId = order?.id || refund.order_id;
    const status = data.status || refund.refund_status;
    const message = `Refund for order #${orderId ? orderId.slice(-8) : ""} status updated to ${status}`;

    const notification = {
      type: "refund",
      title: "Refund Status Updated",
      message: message,
      orderId: orderId,
      data: data,
    };

    dispatch(addNotification(notification));
    toast.info(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  // Listen for review events (Admins and Product Vendors only)
  useSocket("new_review", (data) => {
    const userType = user?.user_type;
    if (userType !== "admin" && userType !== "super_admin" && userType !== "vendor") {
      return;
    }

    const rating = data.rating?.rating;
    const message = `New ${rating ? `${rating}⭐ ` : ""}review received for ${data.product_name || "product"}`;

    const notification = {
      type: "review",
      title: "New Product Review",
      message: message,
      data: data,
    };

    dispatch(addNotification(notification));
    toast.info(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [user]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        dispatch(setNotificationPanelOpen(false));
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, dispatch]);

  const handleBellClick = () => {
    dispatch(toggleNotificationPanel());
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      console.log("🔔 [NOTIFICATION BELL] Marking notification as read:", {
        notificationId: notification.id,
        notificationTitle: notification.title,
      });
      
      // Update Redux state immediately for better UX
      dispatch(markAsRead(notification.id));
      
      // Persist to database
      try {
        const result = await notificationService.markAsRead(notification.id);
        console.log("✅ [NOTIFICATION BELL] Notification marked as read in database:", {
          notificationId: notification.id,
          result: result,
        });
      } catch (error) {
        console.error("❌ [NOTIFICATION BELL] Error marking notification as read:", error);
        console.error("❌ [NOTIFICATION BELL] Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          notificationId: notification.id,
        });
        // Revert Redux state if API call fails
        // Note: We don't revert here to avoid flickering, but the next refresh will correct it
      }
    }

    // Navigate based on notification type
    if (notification.type === "order" && notification.orderId) {
      navigate(`/admin/orders/details/${notification.orderId}`);
      dispatch(setNotificationPanelOpen(false));
    } else if (notification.type === "inventory" && notification.productId) {
      navigate(`/inventory`);
      dispatch(setNotificationPanelOpen(false));
    }
  };

  const handleMarkAllAsRead = async () => {
    console.log("🔔 [NOTIFICATION BELL] Marking all notifications as read");
    
    // Update Redux state immediately for better UX
    dispatch(markAllAsRead());
    
    // Persist to database
    try {
      const response = await notificationService.markAllAsRead();
      console.log("✅ [NOTIFICATION BELL] All notifications marked as read in database:", {
        count: response.data?.count || "unknown count",
        response: response,
      });
    } catch (error) {
      console.error("❌ [NOTIFICATION BELL] Error marking all notifications as read:", error);
      console.error("❌ [NOTIFICATION BELL] Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      // Revert Redux state if API call fails
      // Note: We don't revert here to avoid flickering, but the next refresh will correct it
    }
  };

  const handleRefreshNotifications = async () => {
    const currentToken = token || localStorage.getItem("authToken");
    const currentUser = user;
    
    if (!currentToken || !currentUser) {
      console.warn("⚠️ Cannot refresh: No token or user");
      alert("Please login to view notifications");
      return;
    }

    // Prevent multiple simultaneous refreshes
    if (isLoadingHistoryRef.current) {
      console.log("⏭️ Already refreshing, please wait...");
      return;
    }

    isLoadingHistoryRef.current = true;
    setIsLoadingHistory(true);
    
    try {
      console.log("🔄 Manually refreshing notification history from database...", {
        userId: currentUser.id
      });
      
      // Reset the load key to allow fresh load
      const loadKey = `${currentUser.id}_${currentToken.substring(0, 10)}`;
      hasLoadedHistory.current = null;
      
      const response = await notificationService.getHistory(100);
      console.log("🔄 Refresh Raw API Response:", response);
      console.log("🔄 Refresh Response type:", typeof response);
      console.log("🔄 Refresh Response keys:", Object.keys(response || {}));
      
      // Handle different response structures (same as main load)
      let notifications = [];
      
      if (response && response.success) {
        notifications = response.data?.notifications || [];
        console.log("🔄 Using standard response structure");
      } else if (response && response.data && Array.isArray(response.data.notifications)) {
        notifications = response.data.notifications;
        console.log("🔄 Using nested data structure");
      } else if (Array.isArray(response)) {
        notifications = response;
        console.log("🔄 Using direct array response");
      } else if (response && response.notifications && Array.isArray(response.notifications)) {
        notifications = response.notifications;
        console.log("🔄 Using alternative structure");
      } else {
        console.warn("⚠️ Unexpected response structure:", response);
      }
      
      console.log(`🔄 Refresh: Extracted ${notifications.length} notifications from response`);
      
      if (notifications.length > 0) {
        console.log(`🔄 Refreshed: Found ${notifications.length} notifications`);
        console.log("🔄 First notification:", JSON.stringify(notifications[0], null, 2));
        dispatch(loadNotificationsFromHistory({
          notifications: notifications,
          markAsRead: false,
          clearExisting: true, // Clear and reload fresh
        }));
        hasLoadedHistory.current = loadKey;
        console.log("✅ Notifications refreshed successfully");
      } else {
        console.log("🔄 Refresh: No notifications found in database");
        console.log("🔄 Response was:", response);
        // Clear existing if no notifications found
        dispatch(loadNotificationsFromHistory({
          notifications: [],
          markAsRead: false,
          clearExisting: true,
        }));
        hasLoadedHistory.current = loadKey;
      }
    } catch (error) {
      console.error("❌ Error refreshing notifications:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        stack: error.stack,
      });
      alert(`Error loading notifications: ${error.message}`);
    } finally {
      isLoadingHistoryRef.current = false;
      setIsLoadingHistory(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Just now";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "order":
        return "📦";
      case "inventory":
        return "📊";
      case "payment":
        return "💳";
      default:
        return "🔔";
    }
  };

  return (
    <div className="notification-bell-container" ref={panelRef}>
      <button
        className="notification-bell-btn"
        onClick={handleBellClick}
        title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="notification-badge" title={`${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <h3>Notifications</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="mark-all-read-btn"
                  onClick={handleRefreshNotifications}
                  title="Refresh notifications"
                  style={{ 
                    padding: '4px 8px', 
                    fontSize: '12px',
                    background: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Refresh
                </button>
                {unreadCount > 0 && (
                  <button
                    className="mark-all-read-btn"
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all as read
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="notification-list">
            {isLoadingHistory ? (
              <div className="no-notifications" style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading notifications...</p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                  Fetching from database...
                </p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <p>No notifications</p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
                  {hasLoadedHistory.current 
                    ? `History loaded - No notifications found in database for user: ${user?.id || 'N/A'}` 
                    : 'Click refresh to load history'}
                </p>
                <button 
                  onClick={handleRefreshNotifications}
                  disabled={isLoadingHistory}
                  style={{ 
                    marginTop: '10px', 
                    padding: '6px 12px', 
                    fontSize: '12px',
                    background: isLoadingHistory ? '#ccc' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoadingHistory ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoadingHistory ? 'Loading...' : '🔄 Load from Database'}
                </button>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${
                    !notification.read ? "unread" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-time">
                      {formatTime(notification.timestamp)}
                    </div>
                  </div>
                  {!notification.read && (
                    <div className="notification-dot"></div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;

